import type { FC } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";

import { BaseFlex } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { IPackagesPrice } from "../../types";
import { TRANSLATION_KEYS } from "../constants";
import { getPackagesData } from "../getPackagesData";
import { useStepPackages } from "../useStepPackages";

import {
    Card,
    CheckmarkIcon,
    Container,
    DueToday,
    Feature,
    FeatureList,
    FeatureText,
    Price,
    Subtitle,
    Tag,
    TagContainer,
    Title,
} from "./styledComponent";

interface IPackageItemProps {
    packages?: IPackagesPrice;
}

export const PackageItem: FC<IPackageItemProps> = ({ packages }) => {
    const { translate } = useTranslate();
    const { onSubmitStep } = useStepPackages(packages);

    const renderPrice = () => {
        const priceText = translate(TRANSLATION_KEYS.textPrice);
        return (
            <Price
                fontVariant="boldH4"
                color={colorsPalettes.carbon[500]}
                title={priceText}
            >
                {priceText}
            </Price>
        );
    };

    const renderDueToday = (price: string) => {
        return (
            <DueToday
                fontVariant="report"
                color={colorsPalettes.carbon[300]}
            >
                {translate(TRANSLATION_KEYS.startsFrom, { price })}
            </DueToday>
        );
    };

    return (
        <Container>
            {getPackagesData(packages).map(({ title, subtitle, recommendedTag, price, packageId, list }) => (
                <Card
                    key={packageId}
                    isRecommended={recommendedTag}
                >
                    {recommendedTag ? (
                        <TagContainer>
                            <Tag
                                fontVariant="listItem1"
                                color={colorsPalettes.carbon[0]}
                            >
                                {translate(TRANSLATION_KEYS.recommendedText)}
                            </Tag>
                        </TagContainer>
                    ) : null}
                    <Title
                        fontVariant="tab"
                        color={colorsPalettes.carbon[500]}
                    >
                        {title}
                    </Title>
                    <Subtitle
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                        title={subtitle}
                    >
                        {subtitle}
                    </Subtitle>

                    {renderPrice()}
                    {renderDueToday(price)}

                    <Button
                        width="100%"
                        style={{ marginTop: "24px" }}
                        onClick={() => onSubmitStep(packageId)}
                    >
                        {translate(TRANSLATION_KEYS.buttonText)}
                    </Button>

                    <FeatureList>
                        {list.map((feature, index) => (
                            <BaseFlex>
                                <Feature key={index}>
                                    <CheckmarkIcon color={colorsPalettes.carbon[400]} />
                                    <FeatureText
                                        fontVariant="body2"
                                        color={colorsPalettes.carbon[400]}
                                    >
                                        {feature.item}
                                    </FeatureText>
                                </Feature>
                            </BaseFlex>
                        ))}
                    </FeatureList>
                </Card>
            ))}
        </Container>
    );
};
